import React from 'react';
import { Helmet } from 'react-helmet';

//NOTE - Components
import Hero from './hero';
import LogoBanner from './logo-banner';
import Mission from './mission';
import AboutContentBlock from './about-content-block';

const AboutSections = [
    {
        id: '01',
        heading: 'Get Your Time Back',
        textContent: (
            <>
                Most leaders are buried in the day to day instead of leading the business. <span className="font-bold text-blue10">We change that.</span> Our practitioners embed into your teams across project management, operations, creative, and technical execution — handling the grind so you can focus on what actually matters.
            </>
        ),
        animationName: 'hourglass',
        showContactButton: true
    },
    {
        id: '02',
        heading: 'Maximize Your Profits',
        textContent:'Most businesses lose more than they realize to inefficiency and overhead. We give leaders access to senior practitioners across every function without the overhead of a full-time hire or red tape',
        animationName: 'money',
        showContactButton: true
    },
    {
        id: '03',
        heading: 'Work Smarter',
        textContent:'We plug in as your white-labeled fractional team across project management, operations, creative, and technical execution. We own the work end to end so nothing slips, nothing stalls, and nothing lands back on your desk.',
        animationName: 'lightbulb',
        showContactButton: true
    },
    {
        id: '04',
        heading: 'Access Industry Professionals',
        textContent: 'Beyond project management, we’re an in house team of designers, developers, and marketing professionals. Meaning you can lean on one partner for planning, execution, and delivery across the board.',
        animationName: 'gears',
        showContactButton: true
    }
]

const homeWebPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.partneredmg.com/#webpage",
    "url": "https://www.partneredmg.com/",
    "name": "Fractional Project Management Agency | Partnered Management Group",
    "description": "Partnered Management Group provides fractional project management, PMO services, and operations support. PMP-certified PMs embed into your team — 30% less than a full-time hire.",
    "isPartOf": { "@id": "https://www.partneredmg.com/#website" },
    "about": { "@id": "https://www.partneredmg.com/#organization" },
    "speakable": {
        "@type": "SpeakableSpecification",
        "xpath": [
            "/html/head/title",
            "/html/head/meta[@name='description']/@content"
        ]
    }
};

const homeVideoSchema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": "Partnered Management Group — Fractional Project Management",
    "description": "Partnered Management Group (PMG) provides fractional project management, PMO services, and operations support. PMP-certified practitioners embed into your team at a fraction of the cost of a full-time hire.",
    "thumbnailUrl": "https://www.partneredmg.com/pmg-logo.jpg",
    "uploadDate": "2023-01-01",
    "publisher": {
        "@id": "https://www.partneredmg.com/#organization"
    }
};

const homeFaqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
        {
            "@type": "Question",
            "name": "What is fractional project management?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Fractional project management is a model where PMP-certified project managers embed into your team on a part-time or as-needed basis, providing senior-level execution support at a fraction of the cost of a full-time hire. PMG practitioners take ownership of timelines, communication, coordination, and follow-through so your team can focus on what matters most."
            }
        },
        {
            "@type": "Question",
            "name": "How quickly can PMG start working with my team?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "PMG can onboard and begin delivering value within 24 hours. Our practitioners embed directly into your team's existing workflows and tools, so there is no lengthy ramp-up period. We are up and running fast."
            }
        },
        {
            "@type": "Question",
            "name": "How much does fractional project management cost compared to hiring full-time?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "PMG's fractional project management services are 30% less expensive than hiring a full-time W-2 project manager. There is no overhead for benefits, office space, or downtime. Our clients have saved over $1.2 million in labor expenses while gaining access to PMP-certified senior practitioners."
            }
        },
        {
            "@type": "Question",
            "name": "What types of businesses does PMG work with?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "PMG works with founders and leaders at small to mid-sized businesses who are buried in day-to-day execution and need a trusted partner to plan, execute, and deliver work end to end. Common engagements include scaling operations, launching new initiatives, standing up a PMO, and offloading project delivery. PMG serves businesses nationwide from its base in Charlotte, NC."
            }
        },
        {
            "@type": "Question",
            "name": "What services does Partnered Management Group offer?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Partnered Management Group offers a range of fractional services: Fractional Project Management (PMP-certified PMs embedded into your team), Fractional Operations Management (senior ops support to improve processes and team efficiency), PMO Services via ReadyPMO (a turnkey Project Management Office stood up in 4–6 weeks), Fractional Design and Development (in-house designers and developers available through one partner), and E-commerce Support (end-to-end support for online store operations and growth). Engagements are white-labeled and tailored to each client."
            }
        }
    ]
};

function Home() {
    return (
        <div className='w-screen snap-y snap-mandatory'>
            <Helmet>
                <title>Fractional Project Management Agency | Partnered Management Group</title>
                <meta name="description" content="Partnered Management Group (PMG) provides fractional project management, PMO services, and operations support. PMP-certified PMs embed into your team — 30% less than a full-time hire. Based in Charlotte, NC." />
                <link rel="canonical" href="https://www.partneredmg.com/" />
                <meta property="og:title" content="Fractional Project Management Agency | Partnered Management Group" />
                <meta property="og:description" content="PMP-certified project managers embed into your team to own execution, keep projects in scope, and drive momentum — at a fraction of the cost of a full-time hire." />
                <meta property="og:url" content="https://www.partneredmg.com/" />
                <meta property="og:type" content="website" />
                <script type="application/ld+json">{JSON.stringify(homeWebPageSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(homeVideoSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(homeFaqSchema)}</script>
            </Helmet>
            <Hero />
            <LogoBanner />
            <Mission />
            {AboutSections.map(section => (
                <AboutContentBlock 
                    key={section.id}
                    id={section.id}
                    heading={section.heading}
                    textContent={section.textContent}
                    animationName={section.animationName}
                    showContactButton={section.showContactButton}
                />
            ))}
        </div>
    );
}

export default Home;